import dayjs from 'dayjs';
import _ from 'lodash';
import React from 'react';

import { ModeReglementContext, RegieContext } from '@/contexts';
import {
  getReglementPointageContext,
  type ReglementPointageContextType,
} from '@/contexts/ReglementPointageContext';
import useListeSearchSort from '@/hooks/useListeSearchSort';
import { getReglementsPointage } from '@/routes';
import type { ArretePeriodeModeType, ModeReglementsPointageType, ReglementType } from '@/types';

type ReglementPointageContextProviderProps = {
  children: React.ReactNode;
  code: ModeReglementsPointageType;
  /**
   * Si true, exclut les règlements déjà rattachés à un arrêté périodique finalisé
   * (pointage pour Phase 2 des arrêtés périodiques — évite de ré-afficher des règlements
   * déjà inclus dans un arrêté précédent).
   */
  forArrete?: boolean;
  /**
   * Mode de période de pointage (arrêté périodique). En « libre », les règlements sont
   * filtrés côté client sur `dateFin` (sous-ensemble du fetch [début par défaut, aujourd'hui]).
   */
  periodeMode?: ArretePeriodeModeType;
  /** Borne haute de la période en mode « libre » (reçus créés jusqu'à cette date incluse). */
  dateFin?: dayjs.Dayjs;
};

const ReglementPointageContextProvider = (props: ReglementPointageContextProviderProps) => {
  const { children, code, forArrete = false, periodeMode = 'dernierArrete', dateFin } = props;

  const { selectedRegie } = React.useContext(RegieContext);
  const { regieModesReglement } = React.useContext(ModeReglementContext);

  // Vérifie si le mode de règlement est autorisé pour la régie
  const isModeAutorise = React.useMemo(
    () => _.some(regieModesReglement, { code }),
    [regieModesReglement, code],
  );

  // Obtient le contexte spécifique pour ce code
  const SpecificContext = React.useMemo(() => getReglementPointageContext(code), [code]);

  const {
    items,
    setItems,
    setPage,
    loading,
    sorts,
    setSorts,
    filters,
    setFilters,
    total,
    setTotal,
    reset,
  } = useListeSearchSort({
    active: !_.isNil(selectedRegie) && isModeAutorise,
    getItems: getReglementsPointage,
    fixedParams: forArrete ? { code, pointageArrete: 1 } : { code },
  });

  // En mode « libre » (arrêté périodique), on restreint la liste déjà chargée aux reçus
  // créés jusqu'à la date de fin incluse. C'est un sous-ensemble du fetch initial
  // [début par défaut, aujourd'hui] : pas de re-fetch, filtrage côté client uniquement.
  const filteredReglements = React.useMemo(() => {
    if (forArrete && periodeMode === 'libre' && dateFin) {
      return _.filter(
        items as ReglementType[],
        (r) => !dayjs(r.dateCreationRecu).isAfter(dateFin.endOf('day')),
      );
    }
    return items as ReglementType[];
  }, [items, forArrete, periodeMode, dateFin]);

  const currentContext = React.useMemo<ReglementPointageContextType>(
    () => ({
      total: forArrete && periodeMode === 'libre' ? filteredReglements.length : total,
      reglements: filteredReglements,
      filters,
      sorts,
      loading,
      setReglements: setItems,
      setFilters,
      setSorts,
      setPage,
      setTotal,
      reset,
    }),
    [
      total,
      filteredReglements,
      forArrete,
      periodeMode,
      filters,
      sorts,
      loading,
      setItems,
      setFilters,
      setSorts,
      setPage,
      setTotal,
      reset,
    ],
  );

  return <SpecificContext.Provider value={currentContext}>{children}</SpecificContext.Provider>;
};

export default ReglementPointageContextProvider;
